import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import ColorPicker from '../LoaderGenerator/ColorPicker';
import ViewTree from './ViewTree';
import ViewPropertiesPanel from './ViewPropertiesPanel';
import {
  addViewNode,
  removeViewNode,
  updateViewProperties,
  duplicateViewNode,
  getAvailableViewTypes,
  moveViewNode,
} from './viewTreeUtils';

const CardControls = ({ config, onChange }) => {
  const [viewTypeSelectorOpen, setViewTypeSelectorOpen] = useState(false);
  const [pendingParentId, setPendingParentId] = useState(null);
  const mode = config.mode || 'card';

  const handleModeChange = (newMode) => {
    onChange({ mode: newMode });
  };

  const handleCardStyleChange = (key, value) => {
    onChange({
      cardStyle: {
        ...config.cardStyle,
        [key]: value,
      },
    });
  };

  const handleSelectView = (viewId) => {
    onChange({ selectedViewId: viewId });
  };

  const handleAddView = (parentId) => {
    setPendingParentId(parentId);
    setViewTypeSelectorOpen(true);
  };

  const handleViewTypeSelected = (type) => {
    if (pendingParentId !== undefined) {
      const updatedTree = addViewNode(pendingParentId, type, config.viewTree || []);
      onChange({
        viewTree: updatedTree,
        selectedViewId: updatedTree[updatedTree.length - 1]?.id || null,
      });
    }
    setViewTypeSelectorOpen(false);
    setPendingParentId(null);
  };

  const handleRemoveView = (viewId) => {
    const updatedTree = removeViewNode(viewId, config.viewTree || []);
    onChange({
      viewTree: updatedTree,
      selectedViewId: config.selectedViewId === viewId ? null : config.selectedViewId,
    });
  };

  const handleDuplicateView = (viewId) => {
    const updatedTree = duplicateViewNode(viewId, config.viewTree || []);
    onChange({ viewTree: updatedTree });
  };

  const handleUpdateProperties = (viewId, properties) => {
    const updatedTree = updateViewProperties(viewId, properties, config.viewTree || []);
    onChange({ viewTree: updatedTree });
  };

  const handleMoveView = (viewId, newParentId, newIndex) => {
    const updatedTree = moveViewNode(viewId, newParentId, newIndex, config.viewTree || []);
    onChange({ viewTree: updatedTree });
  };

  const availableTypes = getAvailableViewTypes(mode);

  return (
    <div className="space-y-6">
      {/* Mode Selector */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Development Mode</h3>
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => handleModeChange('card')}
            className={`
              px-4 py-3 rounded-lg text-sm font-medium transition-all
              ${
                mode === 'card'
                  ? 'bg-gradient-primary text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }
            `}
          >
            Card
          </button>
          <button
            onClick={() => handleModeChange('fullUI')}
            className={`
              px-4 py-3 rounded-lg text-sm font-medium transition-all
              ${
                mode === 'fullUI'
                  ? 'bg-gradient-primary text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }
            `}
          >
            Full UI
          </button>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
          {mode === 'card' 
            ? 'Design reusable card components' 
            : 'Design full-screen SwiftUI views'}
        </p>
      </div>

      {/* Card Style Section - Only show in Card mode */}
      {mode === 'card' && (
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Card Style</h3>

        {/* Padding */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Padding: {config.cardStyle?.padding || 16}px
          </label>
          <input
            type="range"
            min="0"
            max="40"
            value={config.cardStyle?.padding || 16}
            onChange={(e) => handleCardStyleChange('padding', parseInt(e.target.value))}
            className="w-full"
          />
        </div>

        {/* Corner Radius */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Corner Radius: {config.cardStyle?.cornerRadius || 12}px
          </label>
          <input
            type="range"
            min="0"
            max="30"
            value={config.cardStyle?.cornerRadius || 12}
            onChange={(e) => handleCardStyleChange('cornerRadius', parseInt(e.target.value))}
            className="w-full"
          />
        </div>

        {/* Shadow */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Shadow Radius: {config.cardStyle?.shadowRadius || 8}px
          </label>
          <input
            type="range"
            min="0"
            max="20"
            value={config.cardStyle?.shadowRadius || 8}
            onChange={(e) => handleCardStyleChange('shadowRadius', parseInt(e.target.value))}
            className="w-full"
          />
          <div className="mt-2">
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
              Shadow Opacity: {config.cardStyle?.shadowOpacity || 0.2}
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={config.cardStyle?.shadowOpacity || 0.2}
              onChange={(e) => handleCardStyleChange('shadowOpacity', parseFloat(e.target.value))}
              className="w-full"
            />
          </div>
        </div>

        {/* Background Type */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Background Type
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {['solid', 'gradient', 'image'].map((type) => (
              <button
                key={type}
                onClick={() => handleCardStyleChange('backgroundType', type)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium transition-all
                  ${
                    config.cardStyle?.backgroundType === type
                      ? 'bg-gradient-primary text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }
                `}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Background Color (for solid) */}
        {config.cardStyle?.backgroundType === 'solid' && (
          <div className="mb-4">
            <ColorPicker
              color={config.cardStyle?.backgroundColor || '#ffffff'}
              onChange={(color) => handleCardStyleChange('backgroundColor', color)}
              label="Background Color"
            />
          </div>
        )}

        {/* Gradient Colors (for gradient) */}
        {config.cardStyle?.backgroundType === 'gradient' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Gradient Colors
            </label>
            <div className="space-y-3">
              <ColorPicker
                color={config.cardStyle?.gradientColors?.[0] || '#F26F29'}
                onChange={(color) => {
                  const newColors = [...(config.cardStyle?.gradientColors || ['#F26F29', '#F03224'])];
                  newColors[0] = color;
                  handleCardStyleChange('gradientColors', newColors);
                }}
                label="Color 1"
              />
              <ColorPicker
                color={config.cardStyle?.gradientColors?.[1] || '#F03224'}
                onChange={(color) => {
                  const newColors = [...(config.cardStyle?.gradientColors || ['#F26F29', '#F03224'])];
                  newColors[1] = color;
                  handleCardStyleChange('gradientColors', newColors);
                }}
                label="Color 2"
              />
            </div>
          </div>
        )}
      </div>
      )}

      {/* View Builder Section */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">View Builder</h3>
        <ViewTree
          viewTree={config.viewTree || []}
          selectedViewId={config.selectedViewId}
          onSelectView={handleSelectView}
          onAddView={handleAddView}
          onRemoveView={handleRemoveView}
          onDuplicateView={handleDuplicateView}
          onMoveView={handleMoveView}
        />
      </div>

      {/* View Properties Panel */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Properties</h3>
        <ViewPropertiesPanel
          viewTree={config.viewTree || []}
          selectedViewId={config.selectedViewId}
          onUpdateProperties={handleUpdateProperties}
        />
      </div>

      {/* View Type Selector Modal - Rendered via Portal to appear above everything */}
      {viewTypeSelectorOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" style={{ zIndex: 9999 }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Select View Type</h3>
            <div className="grid grid-cols-2 gap-2">
              {availableTypes.map(({ type, label }) => (
                <button
                  key={type}
                  onClick={() => handleViewTypeSelected(type)}
                  className="px-4 py-3 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-white transition-all text-sm font-medium"
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                setViewTypeSelectorOpen(false);
                setPendingParentId(null);
              }}
              className="mt-4 w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CardControls;
